import React, { useState, useEffect } from 'react';
import { Calculator, DollarSign, Percent, TrendingUp, Save, Trash2, History, CreditCard, Truck, Receipt, PiggyBank, Plus, MoreHorizontal, Download, FileText, RefreshCw, Globe } from 'lucide-react';

const SalesCalculator = ({ isDark, showToast }) => {
  const [cost, setCost] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [calculations, setCalculations] = useState([]);
  
  // Margin settings
  const [taxRate, setTaxRate] = useState(10);
  const [stripeRate, setStripeRate] = useState(3.5);
  const [shippingCost, setShippingCost] = useState('');
  const [profitMargin, setProfitMargin] = useState(75);
  
  // Toggle which fees to include
  const [includeTax, setIncludeTax] = useState(true);
  const [includeStripe, setIncludeStripe] = useState(true);
  const [includeShipping, setIncludeShipping] = useState(true);
  
  // Misc fees - array of {id, name, amount, isPercent, enabled}
  const [miscFees, setMiscFees] = useState([]);
  const [newFeeName, setNewFeeName] = useState('');
  const [newFeeAmount, setNewFeeAmount] = useState('');
  const [newFeeIsPercent, setNewFeeIsPercent] = useState(false);
  
  // Export file name
  const [exportFileName, setExportFileName] = useState('');
  const [showExportModal, setShowExportModal] = useState(false);
  
  // Save to history modal
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveFileName, setSaveFileName] = useState('');
  
  // Editable price inputs - local state for editing
  const [editingUSD, setEditingUSD] = useState(false);
  const [editingJPY, setEditingJPY] = useState(false);
  const [editUSDValue, setEditUSDValue] = useState('');
  const [editJPYValue, setEditJPYValue] = useState('');
  
  // Exchange rates
  const [exchangeRates, setExchangeRates] = useState({ USD: 1, JPY: 150, EUR: 0.92 });
  const [ratesLastUpdated, setRatesLastUpdated] = useState(null);
  const [loadingRates, setLoadingRates] = useState(false);
  
  // Saved presets
  const [savedPresets, setSavedPresets] = useState([
    { name: 'Standard', profitMargin: 75, taxRate: 10, stripeRate: 3.5 },
    { name: 'Premium', profitMargin: 100, taxRate: 10, stripeRate: 3.5 },
    { name: 'Budget', profitMargin: 50, taxRate: 10, stripeRate: 3.5 },
    { name: 'Wholesale', profitMargin: 25, taxRate: 10, stripeRate: 3.5 },
  ]);
  const [newPresetName, setNewPresetName] = useState('');

  // Fetch exchange rates on mount and cache them
  const fetchExchangeRates = async () => {
    setLoadingRates(true);
    try {
      // Using open.er-api.com - free, reliable, no API key required
      // This API updates daily and provides accurate rates
      const response = await fetch('https://open.er-api.com/v6/latest/USD');
      if (response.ok) {
        const data = await response.json();
        if (data.result === 'success' && data.rates) {
          const newRates = {
            USD: 1,
            JPY: data.rates.JPY,
            EUR: data.rates.EUR
          };
          setExchangeRates(newRates);
          setRatesLastUpdated(new Date(data.time_last_update_utc));
          // Cache rates in localStorage
          localStorage.setItem('exchangeRates', JSON.stringify({
            rates: newRates,
            timestamp: data.time_last_update_utc,
            source: 'open.er-api.com'
          }));
          showToast && showToast(`Exchange rates updated: $1 = ¥${newRates.JPY.toFixed(2)}`);
        } else {
          throw new Error('Invalid API response');
        }
      } else {
        throw new Error('API request failed');
      }
    } catch (error) {
      console.error('Error fetching exchange rates:', error);
      // Try backup API (exchangerate.host)
      try {
        const backupResponse = await fetch('https://api.exchangerate.host/latest?base=USD&symbols=JPY,EUR');
        if (backupResponse.ok) {
          const backupData = await backupResponse.json();
          if (backupData.rates) {
            const newRates = {
              USD: 1,
              JPY: backupData.rates.JPY,
              EUR: backupData.rates.EUR
            };
            setExchangeRates(newRates);
            setRatesLastUpdated(new Date());
            localStorage.setItem('exchangeRates', JSON.stringify({
              rates: newRates,
              timestamp: new Date().toISOString(),
              source: 'exchangerate.host'
            }));
            showToast && showToast(`Exchange rates updated: $1 = ¥${newRates.JPY.toFixed(2)}`);
            setLoadingRates(false);
            return;
          }
        }
      } catch (backupError) {
        console.error('Backup API also failed:', backupError);
      }
      
      // Try to load from cache as last resort
      const cached = localStorage.getItem('exchangeRates');
      if (cached) {
        const { rates, timestamp } = JSON.parse(cached);
        setExchangeRates(rates);
        setRatesLastUpdated(new Date(timestamp));
        showToast && showToast('Using cached exchange rates', 'warning');
      } else {
        showToast && showToast('Could not fetch exchange rates', 'error');
      }
    }
    setLoadingRates(false);
  };

  // Load exchange rates on mount
  useEffect(() => {
    // Clear old cache to force fresh fetch with new API
    const cached = localStorage.getItem('exchangeRates');
    if (cached) {
      try {
        const { rates, timestamp, source } = JSON.parse(cached);
        // If cache is from the old API or older than 4 hours, refresh
        const cacheAge = Date.now() - new Date(timestamp).getTime();
        const fourHours = 4 * 60 * 60 * 1000;
        if (source !== 'open.er-api.com' || cacheAge > fourHours) {
          fetchExchangeRates();
          return;
        }
        // Use recent cache
        setExchangeRates(rates);
        setRatesLastUpdated(new Date(timestamp));
        return;
      } catch (e) {
        // Invalid cache, fetch fresh
      }
    }
    // Fetch fresh rates
    fetchExchangeRates();
  }, []);

  // Load saved presets from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('salesCalculatorPresetsV2');
    if (saved) {
      try {
        setSavedPresets(JSON.parse(saved));
      } catch (e) {
        console.error('Error loading presets:', e);
      }
    }
  }, []);

  // Save presets to localStorage
  const savePresets = (presets) => {
    localStorage.setItem('salesCalculatorPresetsV2', JSON.stringify(presets));
    setSavedPresets(presets);
  };

  // Convert amount between currencies
  const convertCurrency = (amount, fromCurrency, toCurrency) => {
    if (fromCurrency === toCurrency) return amount;
    // Convert to USD first, then to target
    const amountInUSD = fromCurrency === 'USD' ? amount : amount / exchangeRates[fromCurrency];
    return amountInUSD * exchangeRates[toCurrency];
  };

  const currencySymbols = {
    USD: '$',
    JPY: '¥',
    EUR: '€',
    GBP: '£'
  };

  const formatCurrency = (amount) => {
    const symbol = currencySymbols[currency] || '$';
    if (currency === 'JPY') {
      return `${symbol}${Math.round(amount).toLocaleString()}`;
    }
    return `${symbol}${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Misc fees functions
  const addMiscFee = () => {
    if (!newFeeName.trim() || !newFeeAmount) return;
    const newFee = {
      id: Date.now(),
      name: newFeeName.trim(),
      amount: parseFloat(newFeeAmount) || 0,
      isPercent: newFeeIsPercent,
      enabled: true
    };
    setMiscFees([...miscFees, newFee]);
    setNewFeeName('');
    setNewFeeAmount('');
    setNewFeeIsPercent(false);
    showToast && showToast('Fee added');
  };

  const toggleMiscFee = (id) => {
    setMiscFees(miscFees.map(fee => 
      fee.id === id ? { ...fee, enabled: !fee.enabled } : fee
    ));
  };

  const deleteMiscFee = (id) => {
    setMiscFees(miscFees.filter(fee => fee.id !== id));
    showToast && showToast('Fee removed');
  };

  // Calculate all the values
  const costNum = parseFloat(cost) || 0;
  const shippingNum = parseFloat(shippingCost) || 0;
  
  // Step 1: Add profit margin to cost
  const profitAmount = costNum * (profitMargin / 100);
  const priceAfterProfit = costNum + profitAmount;
  
  // Step 2: Add shipping if enabled
  const priceWithShipping = includeShipping ? priceAfterProfit + shippingNum : priceAfterProfit;
  
  // Step 3: Calculate misc fees (can be fixed or percentage)
  const miscFeesCalculated = miscFees.filter(f => f.enabled).map(fee => ({
    ...fee,
    calculatedAmount: fee.isPercent ? priceWithShipping * (fee.amount / 100) : fee.amount
  }));
  const totalMiscFees = miscFeesCalculated.reduce((sum, fee) => sum + fee.calculatedAmount, 0);
  const priceWithMiscFees = priceWithShipping + totalMiscFees;
  
  // Step 4: Calculate Stripe fee (on the subtotal before tax)
  const stripeFeeAmount = includeStripe ? priceWithMiscFees * (stripeRate / 100) : 0;
  const priceWithStripe = priceWithMiscFees + stripeFeeAmount;
  
  // Step 5: Add tax on top of everything
  // Tax is collected from customer and included in the final price
  const taxAmount = includeTax ? priceWithStripe * (taxRate / 100) : 0;
  const finalSellingPrice = priceWithStripe + taxAmount;
  
  // Profit calculations:
  // Gross Margin = What you add on top of cost (profit margin amount + shipping markup if any)
  // Revenue = Final selling price (what customer pays)
  // Stripe takes their cut from the transaction
  // Tax is collected and passed to government (not your profit, but your cash flow includes it)
  
  // Total fees = What's added on top of base price (shipping, misc fees, stripe, tax)
  const totalFees = stripeFeeAmount + taxAmount + (includeShipping ? shippingNum : 0) + totalMiscFees;
  
  // Gross Profit = Profit margin amount (what you marked up from cost)
  const grossProfit = profitAmount;
  
  // Net Profit = Gross profit minus Stripe fee (Stripe is a real cost to you)
  // Tax is pass-through - you collect it and pay it to government, so it's not profit or loss
  const netProfit = grossProfit - stripeFeeAmount;
  
  // Total Revenue (what customer pays)
  const totalRevenue = finalSellingPrice;
  
  // Your take-home (before paying tax to government) = Revenue - Cost - Stripe
  const takeHome = totalRevenue - costNum - stripeFeeAmount - (includeShipping ? 0 : 0); // shipping is paid by customer

  // Calculate converted prices for display
  const finalPriceInUSD = currency === 'USD' ? finalSellingPrice : convertCurrency(finalSellingPrice, currency, 'USD');
  const finalPriceInJPY = currency === 'JPY' ? finalSellingPrice : convertCurrency(finalSellingPrice, currency, 'JPY');

  // Reverse calculation: Given a target final price, calculate the required profit margin
  const calculateProfitMarginFromFinalPrice = (targetFinalPrice, targetCurrency) => {
    if (costNum <= 0) return;
    
    // Convert target price to the current working currency
    let targetInCurrentCurrency = targetFinalPrice;
    if (targetCurrency !== currency) {
      targetInCurrentCurrency = convertCurrency(targetFinalPrice, targetCurrency, currency);
    }
    
    // Work backwards to find the required profit margin
    // Final = priceWithStripe + tax
    // priceWithStripe = Final / (1 + taxRate/100) if tax is included
    let priceBeforeTax = targetInCurrentCurrency;
    if (includeTax) {
      priceBeforeTax = targetInCurrentCurrency / (1 + taxRate / 100);
    }
    
    // priceWithStripe = priceWithMiscFees + stripeFee
    // priceWithMiscFees = priceWithStripe / (1 + stripeRate/100) if stripe is included
    let priceBeforeStripe = priceBeforeTax;
    if (includeStripe) {
      priceBeforeStripe = priceBeforeTax / (1 + stripeRate / 100);
    }
    
    // priceWithMiscFees = priceWithShipping + totalMiscFees
    // For simplicity with misc fees (some are percent-based), we'll iterate to converge
    // But first, let's handle the simpler case without percent-based misc fees
    const fixedMiscFees = miscFees.filter(f => f.enabled && !f.isPercent).reduce((sum, f) => sum + f.amount, 0);
    const percentMiscFees = miscFees.filter(f => f.enabled && f.isPercent);
    
    // If we have percent-based misc fees, this gets complex. Let's approximate.
    let priceBeforeMiscFees = priceBeforeStripe - fixedMiscFees;
    if (percentMiscFees.length > 0) {
      // Iterate to converge on the solution
      for (let i = 0; i < 5; i++) {
        const percentTotal = percentMiscFees.reduce((sum, f) => sum + (priceBeforeMiscFees * f.amount / 100), 0);
        priceBeforeMiscFees = priceBeforeStripe - fixedMiscFees - percentTotal;
      }
    }
    
    // priceWithShipping = cost + profit + shipping
    const priceBeforeShipping = priceBeforeMiscFees - (includeShipping ? shippingNum : 0);
    
    // priceBeforeShipping = cost + profit = cost * (1 + profitMargin/100)
    // So: profitMargin = ((priceBeforeShipping / cost) - 1) * 100
    const requiredProfitMargin = ((priceBeforeShipping / costNum) - 1) * 100;
    
    // Clamp to reasonable values
    if (requiredProfitMargin >= 0 && requiredProfitMargin <= 1000) {
      setProfitMargin(Math.round(requiredProfitMargin * 10) / 10); // Round to 1 decimal
    }
  };

  // Handle USD price editing
  const handleUSDFocus = () => {
    setEditingUSD(true);
    setEditUSDValue(finalPriceInUSD.toFixed(2));
  };

  const handleUSDBlur = () => {
    setEditingUSD(false);
    const numValue = parseFloat(editUSDValue.replace(/[^0-9.]/g, '')) || 0;
    if (numValue > 0 && costNum > 0) {
      calculateProfitMarginFromFinalPrice(numValue, 'USD');
    }
  };

  const handleUSDKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  };

  // Handle JPY price editing
  const handleJPYFocus = () => {
    setEditingJPY(true);
    setEditJPYValue(Math.round(finalPriceInJPY).toString());
  };

  const handleJPYBlur = () => {
    setEditingJPY(false);
    const numValue = parseFloat(editJPYValue.replace(/[^0-9]/g, '')) || 0;
    if (numValue > 0 && costNum > 0) {
      calculateProfitMarginFromFinalPrice(numValue, 'JPY');
    }
  };

  const handleJPYKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  };

  const addToHistory = (name = '') => {
    if (costNum <= 0) return;
    
    const calc = {
      id: Date.now(),
      name: name || `Calculation ${calculations.length + 1}`,
      cost: costNum,
      profitMargin,
      profitAmount,
      shipping: includeShipping ? shippingNum : 0,
      stripeFee: stripeFeeAmount,
      tax: taxAmount,
      selling: finalSellingPrice,
      sellingUSD: finalPriceInUSD,
      sellingJPY: finalPriceInJPY,
      netProfit,
      currency,
      exchangeRate: exchangeRates.JPY,
      timestamp: new Date().toLocaleString()
    };
    
    setCalculations(prev => [calc, ...prev].slice(0, 20));
    showToast && showToast(`"${calc.name}" saved to history`);
    setShowSaveModal(false);
    setSaveFileName('');
  };

  const clearHistory = () => {
    setCalculations([]);
    showToast && showToast('History cleared');
  };

  const addPreset = () => {
    if (!newPresetName.trim()) return;
    const newPresets = [...savedPresets, { 
      name: newPresetName, 
      profitMargin, 
      taxRate, 
      stripeRate 
    }];
    savePresets(newPresets);
    setNewPresetName('');
    showToast && showToast('Preset saved');
  };

  const applyPreset = (preset) => {
    setProfitMargin(preset.profitMargin);
    setTaxRate(preset.taxRate);
    setStripeRate(preset.stripeRate);
  };

  const deletePreset = (index) => {
    const newPresets = savedPresets.filter((_, i) => i !== index);
    savePresets(newPresets);
    showToast && showToast('Preset deleted');
  };

  // Export calculation to file
  const exportCalculation = (filename, costNum, profitAmount, shippingNum, miscFeesCalculated, priceWithMiscFees, stripeFeeAmount, taxAmount, finalSellingPrice, netProfit, totalFees) => {
    const symbol = currencySymbols[currency] || '$';
    const formatNum = (num) => {
      if (currency === 'JPY') return `${symbol}${Math.round(num).toLocaleString()}`;
      return `${symbol}${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };
    
    const now = new Date();
    const dateStr = now.toLocaleDateString();
    const timeStr = now.toLocaleTimeString();
    
    let content = `SALES CALCULATION REPORT
========================
${filename ? `Name: ${filename}` : ''}
Date: ${dateStr} ${timeStr}
Currency: ${currency}

COST BREAKDOWN
--------------
Base Cost: ${formatNum(costNum)}
Profit Margin: ${profitMargin}%
Profit Amount: ${formatNum(profitAmount)}
`;

    if (includeShipping && shippingNum > 0) {
      content += `Shipping: ${formatNum(shippingNum)}\n`;
    }

    if (miscFeesCalculated.length > 0) {
      content += `\nMISC FEES\n---------\n`;
      miscFeesCalculated.forEach(fee => {
        content += `${fee.name}${fee.isPercent ? ` (${fee.amount}%)` : ''}: ${formatNum(fee.calculatedAmount)}\n`;
      });
    }

    content += `
FEES APPLIED
------------`;
    
    if (includeStripe) {
      content += `\nStripe (${stripeRate}%): ${formatNum(stripeFeeAmount)}`;
    }
    if (includeTax) {
      content += `\nTax (${taxRate}%): ${formatNum(taxAmount)}`;
    }

    content += `
Total Fees: ${formatNum(totalFees)}

SUMMARY
-------
Subtotal: ${formatNum(priceWithMiscFees)}
Final Selling Price: ${formatNum(finalSellingPrice)}
Gross Profit: ${formatNum(profitAmount)}
Net Profit: ${formatNum(netProfit)}

------------------------
Generated by Okinawa Sign Studio Sales Calculator
`;

    // Create and download the file
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename || 'calculation'}_${now.toISOString().split('T')[0]}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    showToast && showToast(`Saved as "${link.download}"`);
    setShowExportModal(false);
    setExportFileName('');
  };

  return (
    <div className="space-y-6" data-testid="sales-calculator-tab">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div 
          className="p-3 rounded-xl"
          style={{ 
            background: isDark 
              ? 'linear-gradient(135deg, #00ff88 0%, #00ffff 100%)'
              : 'linear-gradient(135deg, #10b981 0%, #0ea5e9 100%)'
          }}
        >
          <Calculator className="w-6 h-6" style={{ color: isDark ? '#000' : '#fff' }} />
        </div>
        <div>
          <h2 className="text-2xl font-bold" style={{ color: isDark ? '#ffffff' : '#0f172a' }}>
            Sales Calculator
          </h2>
          <p style={{ color: isDark ? '#888' : '#64748b' }}>
            Calculate selling prices with all costs and margins
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Input */}
        <div className="space-y-4">
          {/* Currency & Cost Input */}
          <div 
            className="p-5 rounded-xl"
            style={{
              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f8fafc',
              border: `2px solid ${isDark ? '#333' : '#e2e8f0'}`
            }}
          >
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: isDark ? '#ffffff' : '#0f172a' }}>
              <DollarSign className="w-5 h-5" /> Base Cost
            </h3>

            {/* Currency Selector */}
            <div className="flex gap-2 mb-4">
              {Object.keys(currencySymbols).map(curr => (
                <button
                  key={curr}
                  onClick={() => setCurrency(curr)}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium transition-all"
                  style={{
                    backgroundColor: currency === curr 
                      ? (isDark ? '#00ff88' : '#10b981')
                      : (isDark ? '#222' : '#e2e8f0'),
                    color: currency === curr 
                      ? (isDark ? '#000' : '#fff')
                      : (isDark ? '#888' : '#64748b')
                  }}
                >
                  {currencySymbols[curr]}
                </button>
              ))}
            </div>

            {/* Cost Input */}
            <div className="relative">
              <span 
                className="absolute left-3 top-1/2 -translate-y-1/2 text-xl font-bold"
                style={{ color: isDark ? '#666' : '#94a3b8' }}
              >
                {currencySymbols[currency]}
              </span>
              <input
                type="number"
                value={cost}
                onChange={(e) => setCost(e.target.value)}
                placeholder="0.00"
                className="w-full pl-10 pr-4 py-3 rounded-lg text-xl font-semibold"
                style={{
                  backgroundColor: isDark ? '#1a1a1a' : '#ffffff',
                  border: `2px solid ${isDark ? '#333' : '#e2e8f0'}`,
                  color: isDark ? '#ffffff' : '#0f172a'
                }}
              />
            </div>
            <p className="text-xs mt-2" style={{ color: isDark ? '#666' : '#94a3b8' }}>
              Your cost/purchase price
            </p>
          </div>

          {/* Profit Margin */}
          <div 
            className="p-5 rounded-xl"
            style={{
              backgroundColor: isDark ? 'rgba(0, 255, 136, 0.1)' : 'rgba(16, 185, 129, 0.1)',
              border: `2px solid ${isDark ? '#00ff88' : '#10b981'}`
            }}
          >
            <h3 className="text-lg font-semibold mb-3 flex items-center gap-2" style={{ color: isDark ? '#00ff88' : '#10b981' }}>
              <PiggyBank className="w-5 h-5" /> Profit Margin
            </h3>
            <div className="flex items-center gap-3">
              <input
                type="range"
                min="0"
                max="300"
                value={profitMargin}
                onChange={(e) => setProfitMargin(parseInt(e.target.value))}
                className="flex-1 h-2 rounded-lg appearance-none cursor-pointer"
                style={{
                  background: isDark 
                    ? `linear-gradient(to right, #00ff88 0%, #00ff88 ${profitMargin/3}%, #333 ${profitMargin/3}%, #333 100%)`
                    : `linear-gradient(to right, #10b981 0%, #10b981 ${profitMargin/3}%, #e2e8f0 ${profitMargin/3}%, #e2e8f0 100%)`
                }}
              />
              <div className="flex items-center">
                <input
                  type="number"
                  value={profitMargin}
                  onChange={(e) => setProfitMargin(Math.max(0, parseInt(e.target.value) || 0))}
                  className="w-16 px-2 py-1 rounded text-center text-lg font-bold"
                  style={{
                    backgroundColor: isDark ? '#1a1a1a' : '#ffffff',
                    border: `1px solid ${isDark ? '#00ff88' : '#10b981'}`,
                    color: isDark ? '#00ff88' : '#10b981'
                  }}
                />
                <span className="ml-1 font-bold" style={{ color: isDark ? '#00ff88' : '#10b981' }}>%</span>
              </div>
            </div>
          </div>

          {/* Shipping */}
          <div 
            className="p-5 rounded-xl"
            style={{
              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f8fafc',
              border: `2px solid ${includeShipping ? (isDark ? '#00ffff' : '#0ea5e9') : (isDark ? '#333' : '#e2e8f0')}`
            }}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold flex items-center gap-2" style={{ color: isDark ? '#00ffff' : '#0ea5e9' }}>
                <Truck className="w-5 h-5" /> Shipping
              </h3>
              <button
                onClick={() => setIncludeShipping(!includeShipping)}
                className="px-3 py-1 rounded-full text-xs font-semibold"
                style={{
                  backgroundColor: includeShipping ? (isDark ? '#00ffff' : '#0ea5e9') : (isDark ? '#333' : '#e2e8f0'),
                  color: includeShipping ? '#000' : (isDark ? '#666' : '#94a3b8')
                }}
              >
                {includeShipping ? 'ON' : 'OFF'}
              </button>
            </div>
            <div className="relative">
              <span 
                className="absolute left-3 top-1/2 -translate-y-1/2 font-bold"
                style={{ color: isDark ? '#666' : '#94a3b8' }}
              >
                {currencySymbols[currency]}
              </span>
              <input
                type="number"
                value={shippingCost}
                onChange={(e) => setShippingCost(e.target.value)}
                placeholder="0.00"
                disabled={!includeShipping}
                className="w-full pl-8 pr-4 py-2 rounded-lg"
                style={{
                  backgroundColor: isDark ? '#1a1a1a' : '#ffffff',
                  border: `1px solid ${isDark ? '#333' : '#e2e8f0'}`,
                  color: isDark ? '#ffffff' : '#0f172a',
                  opacity: includeShipping ? 1 : 0.5
                }}
              />
            </div>
          </div>

          {/* Stripe Fee */}
          <div 
            className="p-5 rounded-xl"
            style={{
              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f8fafc',
              border: `2px solid ${includeStripe ? (isDark ? '#635bff' : '#635bff') : (isDark ? '#333' : '#e2e8f0')}`
            }}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold flex items-center gap-2" style={{ color: '#635bff' }}>
                <CreditCard className="w-5 h-5" /> Stripe Fee
              </h3>
              <button
                onClick={() => setIncludeStripe(!includeStripe)}
                className="px-3 py-1 rounded-full text-xs font-semibold"
                style={{
                  backgroundColor: includeStripe ? '#635bff' : (isDark ? '#333' : '#e2e8f0'),
                  color: includeStripe ? '#fff' : (isDark ? '#666' : '#94a3b8')
                }}
              >
                {includeStripe ? 'ON' : 'OFF'}
              </button>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="number"
                step="0.1"
                value={stripeRate}
                onChange={(e) => setStripeRate(parseFloat(e.target.value) || 0)}
                disabled={!includeStripe}
                className="w-20 px-3 py-2 rounded-lg text-center"
                style={{
                  backgroundColor: isDark ? '#1a1a1a' : '#ffffff',
                  border: `1px solid ${isDark ? '#333' : '#e2e8f0'}`,
                  color: isDark ? '#ffffff' : '#0f172a',
                  opacity: includeStripe ? 1 : 0.5
                }}
              />
              <span style={{ color: isDark ? '#888' : '#64748b' }}>%</span>
            </div>
          </div>

          {/* Tax */}
          <div 
            className="p-5 rounded-xl"
            style={{
              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f8fafc',
              border: `2px solid ${includeTax ? (isDark ? '#ff6b6b' : '#ef4444') : (isDark ? '#333' : '#e2e8f0')}`
            }}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold flex items-center gap-2" style={{ color: isDark ? '#ff6b6b' : '#ef4444' }}>
                <Receipt className="w-5 h-5" /> Tax
              </h3>
              <button
                onClick={() => setIncludeTax(!includeTax)}
                className="px-3 py-1 rounded-full text-xs font-semibold"
                style={{
                  backgroundColor: includeTax ? (isDark ? '#ff6b6b' : '#ef4444') : (isDark ? '#333' : '#e2e8f0'),
                  color: includeTax ? '#fff' : (isDark ? '#666' : '#94a3b8')
                }}
              >
                {includeTax ? 'ON' : 'OFF'}
              </button>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="number"
                step="0.1"
                value={taxRate}
                onChange={(e) => setTaxRate(parseFloat(e.target.value) || 0)}
                disabled={!includeTax}
                className="w-20 px-3 py-2 rounded-lg text-center"
                style={{
                  backgroundColor: isDark ? '#1a1a1a' : '#ffffff',
                  border: `1px solid ${isDark ? '#333' : '#e2e8f0'}`,
                  color: isDark ? '#ffffff' : '#0f172a',
                  opacity: includeTax ? 1 : 0.5
                }}
              />
              <span style={{ color: isDark ? '#888' : '#64748b' }}>%</span>
            </div>
          </div>

          {/* Misc Fees */}
          <div 
            className="p-5 rounded-xl"
            style={{
              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f8fafc',
              border: `2px solid ${isDark ? '#f59e0b' : '#f59e0b'}`
            }}
          >
            <h3 className="font-semibold flex items-center gap-2 mb-3" style={{ color: '#f59e0b' }}>
              <MoreHorizontal className="w-5 h-5" /> Misc Fees
            </h3>
            
            {/* Existing misc fees */}
            {miscFees.length > 0 && (
              <div className="space-y-2 mb-3">
                {miscFees.map((fee) => (
                  <div 
                    key={fee.id}
                    className="flex items-center gap-2 p-2 rounded-lg"
                    style={{ 
                      backgroundColor: isDark ? '#1a1a1a' : '#fff',
                      border: `1px solid ${fee.enabled ? '#f59e0b' : (isDark ? '#333' : '#e2e8f0')}`,
                      opacity: fee.enabled ? 1 : 0.6
                    }}
                  >
                    <button
                      onClick={() => toggleMiscFee(fee.id)}
                      className="px-2 py-0.5 rounded text-xs font-semibold"
                      style={{
                        backgroundColor: fee.enabled ? '#f59e0b' : (isDark ? '#333' : '#e2e8f0'),
                        color: fee.enabled ? '#000' : (isDark ? '#666' : '#94a3b8')
                      }}
                    >
                      {fee.enabled ? 'ON' : 'OFF'}
                    </button>
                    <span className="flex-1 text-sm truncate" style={{ color: isDark ? '#ccc' : '#374151' }}>
                      {fee.name}
                    </span>
                    <span className="text-sm font-medium" style={{ color: '#f59e0b' }}>
                      {fee.isPercent ? `${fee.amount}%` : formatCurrency(fee.amount)}
                    </span>
                    <button
                      onClick={() => deleteMiscFee(fee.id)}
                      className="p-1 rounded hover:bg-red-500/20 text-red-500"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            {/* Add new misc fee */}
            <div className="space-y-2">
              <input
                type="text"
                value={newFeeName}
                onChange={(e) => setNewFeeName(e.target.value)}
                placeholder="Fee name (e.g., Installation)"
                className="w-full px-3 py-2 rounded-lg text-sm"
                style={{
                  backgroundColor: isDark ? '#1a1a1a' : '#ffffff',
                  border: `1px solid ${isDark ? '#333' : '#e2e8f0'}`,
                  color: isDark ? '#ffffff' : '#0f172a'
                }}
              />
              <div className="flex gap-2">
                <input
                  type="number"
                  value={newFeeAmount}
                  onChange={(e) => setNewFeeAmount(e.target.value)}
                  placeholder="Amount"
                  className="flex-1 px-3 py-2 rounded-lg text-sm"
                  style={{
                    backgroundColor: isDark ? '#1a1a1a' : '#ffffff',
                    border: `1px solid ${isDark ? '#333' : '#e2e8f0'}`,
                    color: isDark ? '#ffffff' : '#0f172a'
                  }}
                />
                <button
                  onClick={() => setNewFeeIsPercent(!newFeeIsPercent)}
                  className="px-3 py-2 rounded-lg text-sm font-medium"
                  style={{
                    backgroundColor: newFeeIsPercent ? '#f59e0b' : (isDark ? '#333' : '#e2e8f0'),
                    color: newFeeIsPercent ? '#000' : (isDark ? '#888' : '#64748b')
                  }}
                >
                  {newFeeIsPercent ? '%' : currencySymbols[currency]}
                </button>
                <button
                  onClick={addMiscFee}
                  disabled={!newFeeName.trim() || !newFeeAmount}
                  className="px-3 py-2 rounded-lg"
                  style={{
                    backgroundColor: (newFeeName.trim() && newFeeAmount) ? '#f59e0b' : (isDark ? '#333' : '#e2e8f0'),
                    color: (newFeeName.trim() && newFeeAmount) ? '#000' : (isDark ? '#666' : '#94a3b8')
                  }}
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Middle Column - Results */}
        <div 
          className="p-6 rounded-xl"
          style={{
            backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f8fafc',
            border: `2px solid ${isDark ? '#333' : '#e2e8f0'}`
          }}
        >
          <h3 className="text-lg font-semibold mb-4" style={{ color: isDark ? '#ffffff' : '#0f172a' }}>
            Price Breakdown
          </h3>

          {/* Breakdown Table */}
          <div className="space-y-3">
            {/* Base Cost */}
            <div className="flex justify-between items-center py-2 border-b" style={{ borderColor: isDark ? '#333' : '#e2e8f0' }}>
              <span style={{ color: isDark ? '#888' : '#64748b' }}>Base Cost</span>
              <span className="font-semibold" style={{ color: isDark ? '#ff00ff' : '#d946ef' }}>
                {formatCurrency(costNum)}
              </span>
            </div>

            {/* Profit Margin */}
            <div className="flex justify-between items-center py-2 border-b" style={{ borderColor: isDark ? '#333' : '#e2e8f0' }}>
              <span style={{ color: isDark ? '#888' : '#64748b' }}>
                + Profit ({profitMargin}%)
              </span>
              <span className="font-semibold" style={{ color: isDark ? '#00ff88' : '#10b981' }}>
                +{formatCurrency(profitAmount)}
              </span>
            </div>

            {/* Shipping */}
            {includeShipping && shippingNum > 0 && (
              <div className="flex justify-between items-center py-2 border-b" style={{ borderColor: isDark ? '#333' : '#e2e8f0' }}>
                <span style={{ color: isDark ? '#888' : '#64748b' }}>
                  + Shipping
                </span>
                <span className="font-semibold" style={{ color: isDark ? '#00ffff' : '#0ea5e9' }}>
                  +{formatCurrency(shippingNum)}
                </span>
              </div>
            )}

            {/* Misc Fees */}
            {miscFeesCalculated.map((fee) => (
              <div key={fee.id} className="flex justify-between items-center py-2 border-b" style={{ borderColor: isDark ? '#333' : '#e2e8f0' }}>
                <span style={{ color: isDark ? '#888' : '#64748b' }}>
                  + {fee.name} {fee.isPercent ? `(${fee.amount}%)` : ''}
                </span>
                <span className="font-semibold" style={{ color: '#f59e0b' }}>
                  +{formatCurrency(fee.calculatedAmount)}
                </span>
              </div>
            ))}

            {/* Subtotal */}
            <div className="flex justify-between items-center py-2 border-b" style={{ borderColor: isDark ? '#444' : '#cbd5e1' }}>
              <span className="font-medium" style={{ color: isDark ? '#ccc' : '#374151' }}>Subtotal</span>
              <span className="font-semibold" style={{ color: isDark ? '#fff' : '#0f172a' }}>
                {formatCurrency(priceWithMiscFees)}
              </span>
            </div>

            {/* Stripe Fee */}
            {includeStripe && (
              <div className="flex justify-between items-center py-2 border-b" style={{ borderColor: isDark ? '#333' : '#e2e8f0' }}>
                <span style={{ color: isDark ? '#888' : '#64748b' }}>
                  + Stripe ({stripeRate}%)
                </span>
                <span className="font-semibold" style={{ color: '#635bff' }}>
                  +{formatCurrency(stripeFeeAmount)}
                </span>
              </div>
            )}

            {/* Tax */}
            {includeTax && (
              <div className="flex justify-between items-center py-2 border-b" style={{ borderColor: isDark ? '#333' : '#e2e8f0' }}>
                <span style={{ color: isDark ? '#888' : '#64748b' }}>
                  + Tax ({taxRate}%)
                </span>
                <span className="font-semibold" style={{ color: isDark ? '#ff6b6b' : '#ef4444' }}>
                  +{formatCurrency(taxAmount)}
                </span>
              </div>
            )}
          </div>

          {/* Final Selling Price */}
          <div 
            className="mt-6 p-4 rounded-xl text-center"
            style={{ 
              background: isDark 
                ? 'linear-gradient(135deg, rgba(0, 255, 136, 0.2) 0%, rgba(0, 255, 255, 0.2) 100%)'
                : 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(14, 165, 233, 0.2) 100%)',
              border: `2px solid ${isDark ? '#00ff88' : '#10b981'}`
            }}
          >
            <p className="text-sm mb-1" style={{ color: isDark ? '#888' : '#64748b' }}>Final Selling Price</p>
            <p className="text-4xl font-bold" style={{ color: isDark ? '#00ff88' : '#10b981' }}>
              {formatCurrency(finalSellingPrice)}
            </p>
          </div>

          {/* Dual Currency Display - Editable */}
          <div 
            className="mt-3 p-4 rounded-xl"
            style={{ 
              backgroundColor: isDark ? 'rgba(0, 255, 255, 0.05)' : 'rgba(14, 165, 233, 0.05)',
              border: `1px solid ${isDark ? '#00ffff40' : '#0ea5e940'}`
            }}
          >
            {/* Exchange Rate Banner */}
            <div 
              className="flex items-center justify-center gap-3 p-2 rounded-lg mb-3"
              style={{ 
                backgroundColor: isDark ? 'rgba(255, 0, 255, 0.1)' : 'rgba(217, 70, 239, 0.1)',
                border: `1px solid ${isDark ? '#ff00ff40' : '#d946ef40'}`
              }}
            >
              <Globe className="w-4 h-4" style={{ color: isDark ? '#ff00ff' : '#d946ef' }} />
              <span className="font-bold" style={{ color: isDark ? '#ff00ff' : '#d946ef' }}>
                1 USD = ¥{exchangeRates.JPY?.toFixed(2) || '---'} JPY
              </span>
              <button
                onClick={fetchExchangeRates}
                disabled={loadingRates}
                className="flex items-center gap-1 text-xs px-2 py-1 rounded-lg hover:opacity-80 transition-opacity"
                style={{ 
                  backgroundColor: isDark ? '#1a1a1a' : '#fff',
                  color: isDark ? '#888' : '#64748b'
                }}
                title="Refresh exchange rates"
              >
                <RefreshCw className={`w-3 h-3 ${loadingRates ? 'animate-spin' : ''}`} />
              </button>
            </div>

            <p className="text-xs text-center mb-3" style={{ color: isDark ? '#00ffff' : '#0ea5e9' }}>
              Edit either price to auto-calculate profit margin
            </p>
            
            <div className="grid grid-cols-2 gap-3">
              {/* USD Price - Editable */}
              <div 
                className="p-3 rounded-lg"
                style={{ 
                  backgroundColor: isDark ? '#1a1a1a' : '#fff',
                  border: `2px solid ${isDark ? '#00ff88' : '#10b981'}`
                }}
              >
                <p className="text-xs mb-1 text-center" style={{ color: isDark ? '#888' : '#64748b' }}>
                  US Dollar (click to edit)
                </p>
                <div className="flex items-center justify-center gap-1">
                  <span className="text-2xl font-bold" style={{ color: isDark ? '#00ff88' : '#10b981' }}>$</span>
                  <input
                    type="text"
                    value={editingUSD ? editUSDValue : finalPriceInUSD.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    onChange={(e) => setEditUSDValue(e.target.value)}
                    onFocus={handleUSDFocus}
                    onBlur={handleUSDBlur}
                    onKeyDown={handleUSDKeyDown}
                    className="text-2xl font-bold w-full text-center bg-transparent outline-none cursor-text"
                    style={{ 
                      color: isDark ? '#00ff88' : '#10b981',
                      caretColor: isDark ? '#00ff88' : '#10b981'
                    }}
                    disabled={costNum <= 0}
                    data-testid="usd-price-input"
                  />
                </div>
              </div>
              
              {/* JPY Price - Editable */}
              <div 
                className="p-3 rounded-lg"
                style={{ 
                  backgroundColor: isDark ? '#1a1a1a' : '#fff',
                  border: `2px solid ${isDark ? '#ff00ff' : '#d946ef'}`
                }}
              >
                <p className="text-xs mb-1 text-center" style={{ color: isDark ? '#888' : '#64748b' }}>
                  Japanese Yen (click to edit)
                </p>
                <div className="flex items-center justify-center gap-1">
                  <span className="text-2xl font-bold" style={{ color: isDark ? '#ff00ff' : '#d946ef' }}>¥</span>
                  <input
                    type="text"
                    value={editingJPY ? editJPYValue : Math.round(finalPriceInJPY).toLocaleString()}
                    onChange={(e) => setEditJPYValue(e.target.value)}
                    onFocus={handleJPYFocus}
                    onBlur={handleJPYBlur}
                    onKeyDown={handleJPYKeyDown}
                    className="text-2xl font-bold w-full text-center bg-transparent outline-none cursor-text"
                    style={{ 
                      color: isDark ? '#ff00ff' : '#d946ef',
                      caretColor: isDark ? '#ff00ff' : '#d946ef'
                    }}
                    disabled={costNum <= 0}
                    data-testid="jpy-price-input"
                  />
                </div>
              </div>
            </div>
            
            {/* Last Updated */}
            {ratesLastUpdated && (
              <p className="text-xs text-center mt-2" style={{ color: isDark ? '#555' : '#94a3b8' }}>
                Rate updated: {ratesLastUpdated.toLocaleDateString()} {ratesLastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </p>
            )}
          </div>

          {/* Profit Summary */}
          <div className="mt-4 grid grid-cols-2 gap-3">
            <div 
              className="p-3 rounded-lg text-center"
              style={{ backgroundColor: isDark ? 'rgba(255, 255, 0, 0.1)' : 'rgba(234, 179, 8, 0.1)' }}
            >
              <p className="text-xs" style={{ color: isDark ? '#888' : '#64748b' }}>Gross Profit</p>
              <p className="text-xl font-bold" style={{ color: isDark ? '#ffff00' : '#eab308' }}>
                {formatCurrency(grossProfit)}
              </p>
              <p className="text-sm font-semibold mt-1" style={{ color: isDark ? '#ff00ff' : '#d946ef' }}>
                ¥{Math.round(convertCurrency(grossProfit, currency, 'JPY')).toLocaleString()}
              </p>
              <p className="text-xs mt-1" style={{ color: isDark ? '#555' : '#94a3b8' }}>
                (Your markup)
              </p>
            </div>
            <div 
              className="p-3 rounded-lg text-center"
              style={{ backgroundColor: isDark ? 'rgba(0, 255, 136, 0.1)' : 'rgba(16, 185, 129, 0.1)' }}
            >
              <p className="text-xs" style={{ color: isDark ? '#888' : '#64748b' }}>Net Profit</p>
              <p className="text-xl font-bold" style={{ color: netProfit >= 0 ? (isDark ? '#00ff88' : '#10b981') : (isDark ? '#ff6b6b' : '#ef4444') }}>
                {formatCurrency(netProfit)}
              </p>
              <p className="text-sm font-semibold mt-1" style={{ color: isDark ? '#ff00ff' : '#d946ef' }}>
                ¥{Math.round(convertCurrency(netProfit, currency, 'JPY')).toLocaleString()}
              </p>
              <p className="text-xs mt-1" style={{ color: isDark ? '#555' : '#94a3b8' }}>
                (After Stripe: -{formatCurrency(stripeFeeAmount)})
              </p>
            </div>
          </div>

          {/* Customer Pays vs You Keep */}
          <div className="mt-3 p-4 rounded-lg" style={{ 
            backgroundColor: isDark ? 'rgba(0, 255, 255, 0.05)' : 'rgba(14, 165, 233, 0.05)',
            border: `1px dashed ${isDark ? '#00ffff40' : '#0ea5e940'}`
          }}>
            <div className="flex justify-between items-center">
              <div>
                <p className="text-xs" style={{ color: isDark ? '#888' : '#64748b' }}>Customer Pays</p>
                <p className="font-bold" style={{ color: isDark ? '#00ffff' : '#0ea5e9' }}>{formatCurrency(finalSellingPrice)}</p>
              </div>
              <div className="text-center">
                <p className="text-xs" style={{ color: isDark ? '#888' : '#64748b' }}>You Keep</p>
                <p className="font-bold" style={{ color: isDark ? '#00ff88' : '#10b981' }}>{formatCurrency(finalSellingPrice - stripeFeeAmount - taxAmount)}</p>
              </div>
              <div className="text-right">
                <p className="text-xs" style={{ color: isDark ? '#888' : '#64748b' }}>Tax to Govt</p>
                <p className="font-bold" style={{ color: isDark ? '#ff6b6b' : '#ef4444' }}>{formatCurrency(taxAmount)}</p>
              </div>
            </div>
          </div>

          {/* Total Fees */}
          <div 
            className="mt-3 p-3 rounded-lg text-center"
            style={{ backgroundColor: isDark ? 'rgba(255, 107, 107, 0.1)' : 'rgba(239, 68, 68, 0.1)' }}
          >
            <p className="text-xs" style={{ color: isDark ? '#888' : '#64748b' }}>Total Fees & Additions</p>
            <p className="text-lg font-bold" style={{ color: isDark ? '#ff6b6b' : '#ef4444' }}>
              {formatCurrency(totalFees)}
            </p>
          </div>

          {/* Action Buttons */}
          <div className="mt-4 flex gap-2">
            {/* Save to History */}
            <button
              onClick={() => setShowSaveModal(true)}
              disabled={costNum <= 0}
              className="flex-1 py-3 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all"
              style={{
                background: costNum > 0 
                  ? (isDark ? 'linear-gradient(135deg, #00ffff 0%, #00ff88 100%)' : 'linear-gradient(135deg, #0ea5e9 0%, #10b981 100%)')
                  : (isDark ? '#333' : '#e2e8f0'),
                color: costNum > 0 ? (isDark ? '#000' : '#fff') : (isDark ? '#666' : '#94a3b8'),
                opacity: costNum > 0 ? 1 : 0.5
              }}
              data-testid="save-calculation-btn"
            >
              <Save className="w-4 h-4" /> Save
            </button>
            
            {/* Export Button */}
            <button
              onClick={() => setShowExportModal(true)}
              disabled={costNum <= 0}
              className="flex-1 py-3 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all"
              style={{
                background: costNum > 0 
                  ? (isDark ? 'linear-gradient(135deg, #ff00ff 0%, #00ffff 100%)' : 'linear-gradient(135deg, #d946ef 0%, #0ea5e9 100%)')
                  : (isDark ? '#333' : '#e2e8f0'),
                color: costNum > 0 ? (isDark ? '#000' : '#fff') : (isDark ? '#666' : '#94a3b8'),
                opacity: costNum > 0 ? 1 : 0.5
              }}
              data-testid="export-calculation-btn"
            >
              <Download className="w-4 h-4" /> Export
            </button>
          </div>

          {/* Export Modal */}
          {showExportModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <div 
                className="absolute inset-0 bg-black/60 backdrop-blur-sm" 
                onClick={() => setShowExportModal(false)} 
              />
              <div 
                className="relative w-full max-w-md rounded-xl p-6"
                style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff' }}
              >
                <h3 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ color: isDark ? '#fff' : '#0f172a' }}>
                  <FileText className="w-5 h-5" style={{ color: isDark ? '#ff00ff' : '#d946ef' }} />
                  Export Calculation
                </h3>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2" style={{ color: isDark ? '#ccc' : '#475569' }}>
                    File Name
                  </label>
                  <input
                    type="text"
                    value={exportFileName}
                    onChange={(e) => setExportFileName(e.target.value)}
                    placeholder="e.g., Client_Quote_April"
                    className="w-full p-3 rounded-lg"
                    style={{
                      backgroundColor: isDark ? '#222' : '#f8fafc',
                      color: isDark ? '#fff' : '#000',
                      border: `1px solid ${isDark ? '#444' : '#e2e8f0'}`
                    }}
                    data-testid="export-filename-input"
                    autoFocus
                  />
                  <p className="text-xs mt-1" style={{ color: isDark ? '#666' : '#94a3b8' }}>
                    File will be saved as: {exportFileName || 'calculation'}_{new Date().toISOString().split('T')[0]}.txt
                  </p>
                </div>

                {/* Preview Summary */}
                <div 
                  className="p-3 rounded-lg mb-4"
                  style={{ 
                    backgroundColor: isDark ? 'rgba(0, 255, 136, 0.1)' : 'rgba(16, 185, 129, 0.1)',
                    border: `1px solid ${isDark ? '#00ff88' : '#10b981'}`
                  }}
                >
                  <p className="text-sm" style={{ color: isDark ? '#888' : '#64748b' }}>Summary Preview</p>
                  <p className="text-lg font-bold" style={{ color: isDark ? '#00ff88' : '#10b981' }}>
                    Cost: {formatCurrency(costNum)} → Sell: {formatCurrency(finalSellingPrice)}
                  </p>
                  <p className="text-sm" style={{ color: isDark ? '#00ffff' : '#0ea5e9' }}>
                    Profit: {formatCurrency(netProfit)} ({profitMargin}% margin)
                  </p>
                </div>
                
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowExportModal(false)}
                    className="flex-1 py-2 rounded-lg font-medium"
                    style={{
                      backgroundColor: isDark ? '#333' : '#e2e8f0',
                      color: isDark ? '#ccc' : '#475569'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => exportCalculation(
                      exportFileName, 
                      costNum, 
                      profitAmount, 
                      shippingNum, 
                      miscFeesCalculated, 
                      priceWithMiscFees, 
                      stripeFeeAmount, 
                      taxAmount, 
                      finalSellingPrice, 
                      netProfit, 
                      totalFees
                    )}
                    className="flex-1 py-2 rounded-lg font-medium flex items-center justify-center gap-2"
                    style={{
                      background: isDark 
                        ? 'linear-gradient(135deg, #ff00ff 0%, #00ffff 100%)'
                        : 'linear-gradient(135deg, #d946ef 0%, #0ea5e9 100%)',
                      color: isDark ? '#000' : '#fff'
                    }}
                    data-testid="confirm-export-btn"
                  >
                    <Download className="w-4 h-4" /> Download
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Save Modal */}
          {showSaveModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <div 
                className="absolute inset-0 bg-black/60 backdrop-blur-sm" 
                onClick={() => setShowSaveModal(false)} 
              />
              <div 
                className="relative w-full max-w-md rounded-xl p-6"
                style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff' }}
              >
                <h3 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ color: isDark ? '#fff' : '#0f172a' }}>
                  <Save className="w-5 h-5" style={{ color: isDark ? '#00ffff' : '#0ea5e9' }} />
                  Save Calculation
                </h3>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2" style={{ color: isDark ? '#ccc' : '#475569' }}>
                    Name this calculation
                  </label>
                  <input
                    type="text"
                    value={saveFileName}
                    onChange={(e) => setSaveFileName(e.target.value)}
                    placeholder="e.g., Neon Sign Quote, Client ABC"
                    className="w-full p-3 rounded-lg"
                    style={{
                      backgroundColor: isDark ? '#222' : '#f8fafc',
                      color: isDark ? '#fff' : '#000',
                      border: `1px solid ${isDark ? '#444' : '#e2e8f0'}`
                    }}
                    data-testid="save-filename-input"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        addToHistory(saveFileName);
                      }
                    }}
                  />
                </div>

                {/* Summary Preview */}
                <div 
                  className="p-4 rounded-lg mb-4"
                  style={{ 
                    backgroundColor: isDark ? 'rgba(0, 255, 136, 0.1)' : 'rgba(16, 185, 129, 0.1)',
                    border: `1px solid ${isDark ? '#00ff88' : '#10b981'}`
                  }}
                >
                  <div className="grid grid-cols-2 gap-3 text-center">
                    <div>
                      <p className="text-xs" style={{ color: isDark ? '#888' : '#64748b' }}>Cost</p>
                      <p className="font-bold" style={{ color: isDark ? '#fff' : '#0f172a' }}>{formatCurrency(costNum)}</p>
                    </div>
                    <div>
                      <p className="text-xs" style={{ color: isDark ? '#888' : '#64748b' }}>Profit %</p>
                      <p className="font-bold" style={{ color: isDark ? '#ffff00' : '#eab308' }}>{profitMargin}%</p>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t grid grid-cols-2 gap-3 text-center" style={{ borderColor: isDark ? '#333' : '#e2e8f0' }}>
                    <div>
                      <p className="text-xs" style={{ color: isDark ? '#888' : '#64748b' }}>USD Price</p>
                      <p className="text-lg font-bold" style={{ color: isDark ? '#00ff88' : '#10b981' }}>
                        ${finalPriceInUSD.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs" style={{ color: isDark ? '#888' : '#64748b' }}>JPY Price</p>
                      <p className="text-lg font-bold" style={{ color: isDark ? '#ff00ff' : '#d946ef' }}>
                        ¥{Math.round(finalPriceInJPY).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <p className="text-xs text-center mt-2" style={{ color: isDark ? '#555' : '#94a3b8' }}>
                    Rate: $1 = ¥{exchangeRates.JPY?.toFixed(2)}
                  </p>
                </div>
                
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowSaveModal(false)}
                    className="flex-1 py-2 rounded-lg font-medium"
                    style={{
                      backgroundColor: isDark ? '#333' : '#e2e8f0',
                      color: isDark ? '#ccc' : '#475569'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => addToHistory(saveFileName)}
                    className="flex-1 py-2 rounded-lg font-medium flex items-center justify-center gap-2"
                    style={{
                      background: isDark 
                        ? 'linear-gradient(135deg, #00ffff 0%, #00ff88 100%)'
                        : 'linear-gradient(135deg, #0ea5e9 0%, #10b981 100%)',
                      color: isDark ? '#000' : '#fff'
                    }}
                    data-testid="confirm-save-btn"
                  >
                    <Save className="w-4 h-4" /> Save
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right Column - Presets & History */}
        <div className="space-y-4">
          {/* Quick Presets */}
          <div 
            className="p-5 rounded-xl"
            style={{
              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f8fafc',
              border: `2px solid ${isDark ? '#333' : '#e2e8f0'}`
            }}
          >
            <h3 className="font-semibold mb-3 flex items-center gap-2" style={{ color: isDark ? '#ffffff' : '#0f172a' }}>
              <TrendingUp className="w-5 h-5" /> Quick Presets
            </h3>
            
            <div className="space-y-2 mb-4">
              {savedPresets.map((preset, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <button
                    onClick={() => applyPreset(preset)}
                    className="flex-1 px-3 py-2 rounded-lg text-sm font-medium text-left transition-all hover:scale-[1.02]"
                    style={{
                      backgroundColor: isDark ? '#1a1a1a' : '#fff',
                      border: `1px solid ${isDark ? '#333' : '#e2e8f0'}`,
                      color: isDark ? '#ccc' : '#374151'
                    }}
                  >
                    <span className="font-semibold">{preset.name}</span>
                    <span className="text-xs ml-2" style={{ color: isDark ? '#666' : '#94a3b8' }}>
                      {preset.profitMargin}% + {preset.taxRate}% tax + {preset.stripeRate}% stripe
                    </span>
                  </button>
                  {idx >= 4 && (
                    <button
                      onClick={() => deletePreset(idx)}
                      className="p-2 rounded hover:bg-red-500/20 text-red-500"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* Add New Preset */}
            <div className="flex gap-2">
              <input
                type="text"
                value={newPresetName}
                onChange={(e) => setNewPresetName(e.target.value)}
                placeholder="Preset name..."
                className="flex-1 px-3 py-2 rounded-lg text-sm"
                style={{
                  backgroundColor: isDark ? '#1a1a1a' : '#ffffff',
                  border: `1px solid ${isDark ? '#333' : '#e2e8f0'}`,
                  color: isDark ? '#ffffff' : '#0f172a'
                }}
              />
              <button
                onClick={addPreset}
                disabled={!newPresetName.trim()}
                className="px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap"
                style={{
                  backgroundColor: isDark ? '#00ffff' : '#0ea5e9',
                  color: isDark ? '#000' : '#fff',
                  opacity: newPresetName.trim() ? 1 : 0.5
                }}
              >
                Save
              </button>
            </div>
          </div>

          {/* Calculation History */}
          <div 
            className="p-5 rounded-xl"
            style={{
              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f8fafc',
              border: `2px solid ${isDark ? '#333' : '#e2e8f0'}`
            }}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold flex items-center gap-2" style={{ color: isDark ? '#ffffff' : '#0f172a' }}>
                <History className="w-5 h-5" /> History
              </h3>
              {calculations.length > 0 && (
                <button
                  onClick={clearHistory}
                  className="text-xs px-2 py-1 rounded hover:bg-red-500/20 text-red-500"
                >
                  Clear
                </button>
              )}
            </div>

            {calculations.length === 0 ? (
              <p className="text-center py-6 text-sm" style={{ color: isDark ? '#666' : '#94a3b8' }}>
                No calculations yet
              </p>
            ) : (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {calculations.map((calc) => (
                  <div 
                    key={calc.id}
                    className="p-3 rounded-lg cursor-pointer hover:opacity-80 transition-opacity"
                    style={{
                      backgroundColor: isDark ? '#1a1a1a' : '#ffffff',
                      border: `1px solid ${isDark ? '#333' : '#e2e8f0'}`
                    }}
                    onClick={() => {
                      setCost(calc.cost.toString());
                      setProfitMargin(calc.profitMargin);
                      setCurrency(calc.currency);
                    }}
                  >
                    {/* Name */}
                    <p className="font-semibold text-sm mb-1" style={{ color: isDark ? '#fff' : '#0f172a' }}>
                      {calc.name || 'Untitled'}
                    </p>
                    
                    {/* Prices in both currencies */}
                    <div className="flex justify-between items-center">
                      <div className="flex gap-3">
                        <span className="font-bold" style={{ color: isDark ? '#00ff88' : '#10b981' }}>
                          ${(calc.sellingUSD || calc.selling).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                        <span className="font-bold" style={{ color: isDark ? '#ff00ff' : '#d946ef' }}>
                          ¥{Math.round(calc.sellingJPY || calc.selling).toLocaleString()}
                        </span>
                      </div>
                      <span className="text-xs px-2 py-0.5 rounded" style={{ 
                        backgroundColor: isDark ? 'rgba(0, 255, 136, 0.1)' : 'rgba(16, 185, 129, 0.1)',
                        color: isDark ? '#00ff88' : '#10b981' 
                      }}>
                        +{calc.profitMargin}%
                      </span>
                    </div>
                    
                    {/* Details */}
                    <div className="flex justify-between items-center mt-1">
                      <p className="text-xs" style={{ color: isDark ? '#555' : '#94a3b8' }}>
                        Cost: {currencySymbols[calc.currency]}{calc.cost.toLocaleString()} • Net: {currencySymbols[calc.currency]}{calc.netProfit?.toLocaleString(undefined, { minimumFractionDigits: calc.currency === 'JPY' ? 0 : 2 }) || '0'}
                      </p>
                      {calc.exchangeRate && (
                        <p className="text-xs" style={{ color: isDark ? '#444' : '#a1a1aa' }}>
                          ¥{calc.exchangeRate?.toFixed(0)}/USD
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Reference */}
      <div 
        className="p-4 rounded-xl"
        style={{
          backgroundColor: isDark ? 'rgba(0, 255, 136, 0.05)' : 'rgba(16, 185, 129, 0.05)',
          border: `1px solid ${isDark ? '#00ff8840' : '#10b98140'}`
        }}
      >
        <h4 className="font-semibold mb-2" style={{ color: isDark ? '#00ff88' : '#10b981' }}>
          Quick Reference for {formatCurrency(costNum || 100)}
        </h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[50, 75, 100, 150].map(margin => {
            const baseCost = costNum || 100;
            const profit = baseCost * (margin / 100);
            const sub = baseCost + profit + (includeShipping ? shippingNum : 0);
            const stripe = includeStripe ? sub * (stripeRate / 100) : 0;
            const tax = includeTax ? (sub + stripe) * (taxRate / 100) : 0;
            const total = sub + stripe + tax;
            return (
              <button
                key={margin}
                onClick={() => setProfitMargin(margin)}
                className="p-3 rounded-lg text-center transition-all hover:scale-[1.02]"
                style={{
                  backgroundColor: profitMargin === margin 
                    ? (isDark ? 'rgba(0, 255, 136, 0.2)' : 'rgba(16, 185, 129, 0.2)')
                    : (isDark ? '#1a1a1a' : '#fff'),
                  border: `1px solid ${profitMargin === margin ? (isDark ? '#00ff88' : '#10b981') : (isDark ? '#333' : '#e2e8f0')}`
                }}
              >
                <p className="text-sm font-medium" style={{ color: isDark ? '#888' : '#64748b' }}>{margin}% margin</p>
                <p className="text-lg font-bold" style={{ color: isDark ? '#00ff88' : '#10b981' }}>
                  {formatCurrency(total)}
                </p>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SalesCalculator;
